import { DESIRED_BUCKETS, MIN_GRANULARITY_SECONDS, ONE_MINUTE } from 'constants/charting';
import type { HistoricalForecasts, MarketForecastHistoryVo } from 'models/historical_data';

const currentUnixTime = (): number => Math.round(Date.now() / 1000);

const periodIntervalToSeconds = (periodInterval: string): number => {
    const parsed = parseInt(periodInterval, 10);
    return parsed === 0 ? 5 : parsed * ONE_MINUTE;
};

const warnedDroppedCandleMarkets = new Set<string>();

const sanitizeForecastVos = (forecasts: MarketForecastHistoryVo[]): MarketForecastHistoryVo[] => {
    const finite = forecasts.filter(
        (vo) => Number.isFinite(vo.raw_numerical_forecast) && Number.isFinite(vo.end_period_ts),
    );
    const dropped = forecasts.length - finite.length;
    if (dropped > 0) {
        const ticker = forecasts[0]?.market_ticker ?? 'unknown market';
        if (!warnedDroppedCandleMarkets.has(ticker)) {
            warnedDroppedCandleMarkets.add(ticker);
            console.warn(`[forecastHistory] dropped ${dropped} non-finite candle(s) for ${ticker}`);
        }
    }
    return finite;
};

export const mapVosToHistoricalForecasts = (forecasts: MarketForecastHistoryVo[]): HistoricalForecasts[] =>
    sanitizeForecastVos(forecasts).map((vo) => ({
        eventTicker: vo.event_ticker,
        marketTicker: vo.market_ticker,
        rawNumericalForecast: vo.raw_numerical_forecast,
        numericalForecast: vo.numerical_forecast,
        formattedForecast: vo.formatted_forecast,
        ts: vo.end_period_ts - periodIntervalToSeconds(vo.period_interval),
        periodInterval: vo.period_interval,
    }));

/** Pure, synchronous counterpart to the browser worker used by the client query. */
export const extrapolateForecastsHistory = (
    forecastHistory: HistoricalForecasts[],
    nowTs?: number,
): HistoricalForecasts[] => {
    if (forecastHistory.length === 0) return [];

    const history = forecastHistory.slice();
    if (history.length === 1) history.push({ ...history[0], ts: nowTs ?? currentUnixTime() });

    const firstTs = history[0].ts;
    const lastTs = history[history.length - 1].ts;
    const extrapolated: HistoricalForecasts[] = [];
    let intervalSeconds = Math.floor((lastTs - firstTs) / DESIRED_BUCKETS);
    intervalSeconds = Math.max(MIN_GRANULARITY_SECONDS, intervalSeconds - (intervalSeconds % ONE_MINUTE));

    let currentTs = firstTs;
    let currentIndex = 0;
    while (currentTs <= lastTs) {
        const currentItem = history[currentIndex];
        if (!currentItem) break;
        currentTs = currentItem.ts;

        if (currentIndex === history.length - 1) {
            extrapolated.push(currentItem);
            break;
        }

        const nextItem = history[currentIndex + 1];
        const startOfWindow = currentTs - (currentTs % intervalSeconds);
        const itemsInWindow = history.filter(
            (item) => item.ts >= startOfWindow && item.ts < startOfWindow + intervalSeconds,
        );
        const windowCoversEnd = currentIndex + itemsInWindow.length >= history.length;

        if (itemsInWindow.length > 1 && !windowCoversEnd) {
            // The opening candle stays as-is, so a line opens on its history's first value however dense it is.
            const opening = currentIndex === 0;
            if (opening) extrapolated.push(currentItem);
            const averaged = opening ? itemsInWindow.slice(1) : itemsInWindow;
            const average = averaged.reduce((sum, item) => sum + item.rawNumericalForecast, 0) / averaged.length;
            extrapolated.push({ ...averaged[0], rawNumericalForecast: average, numericalForecast: average });
            currentIndex += itemsInWindow.length;
            continue;
        }

        extrapolated.push(currentItem);
        while (nextItem && nextItem.ts > currentTs + intervalSeconds) {
            const addedItem = { ...currentItem, ts: currentTs + intervalSeconds };
            extrapolated.push(addedItem);
            currentTs += intervalSeconds;
        }
        currentIndex++;
    }

    return extrapolated;
};

export const mapForecastsHistory = (forecasts: MarketForecastHistoryVo[], nowTs?: number): HistoricalForecasts[] =>
    extrapolateForecastsHistory(mapVosToHistoricalForecasts(forecasts), nowTs);
